import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getErrorMessage } from '@/lib/api-errors'
import { toast } from '@/lib/toast'

import {
  approveChangeRequest,
  listAdminChangeRequests,
  rejectChangeRequest,
} from '../api/changeRequestsApi'

import type {
  ListAdminChangeRequestsParams,
  RejectChangeRequestBody,
} from '../api/changeRequestsApi'

export function adminChangeRequestsQueryOptions(
  params: ListAdminChangeRequestsParams = {},
) {
  return queryOptions({
    queryKey: ['admin', 'change-requests', params] as const,
    queryFn: () => listAdminChangeRequests(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

export function useAdminChangeRequests(params?: ListAdminChangeRequestsParams) {
  return useQuery(adminChangeRequestsQueryOptions(params ?? {}))
}

// Invalidate both the admin queue and the user-side view of the same
// request — a single approval/rejection affects both surfaces.
function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'] })
  queryClient.invalidateQueries({ queryKey: ['users', 'me', 'change-requests'] })
  queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
  queryClient.invalidateQueries({ queryKey: ['users'] })
}

export function useApproveChangeRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => approveChangeRequest(id),
    onSuccess: () => invalidateAll(queryClient),
    onError: (err) =>
      toast.error(getErrorMessage(err, 'Could not approve this request.')),
  })
}

export function useRejectChangeRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; body: RejectChangeRequestBody }) =>
      rejectChangeRequest(vars.id, vars.body),
    onSuccess: () => invalidateAll(queryClient),
    onError: (err) =>
      toast.error(getErrorMessage(err, 'Could not reject this request.')),
  })
}
